import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib'
import { supabase } from '../lib/supabase'

const teal = '#0D9488'
const plum = '#1E1040'

const TAG_ACCENTS = {
  schedule_c: { rgb: [13, 148, 136], hex: '#0D9488', name: 'Schedule C' },
  company_card: { rgb: [37, 99, 235], hex: '#2563EB', name: 'Company Card' },
  reimbursable: { rgb: [245, 158, 11], hex: '#F59E0B', name: 'Reimbursable' },
  personal: { rgb: [168, 85, 247], hex: '#A855F7', name: 'Personal' },
}

export default function Reports() {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [year, setYear] = useState(new Date().getFullYear())
  const [userName, setUserName] = useState('')
  const [userEmail, setUserEmail] = useState('')

  const [tags, setTags] = useState([])
  const [paymentMethods, setPaymentMethods] = useState([])
  const [receipts, setReceipts] = useState([])
  const [trips, setTrips] = useState([])

  const [availableYears, setAvailableYears] = useState([])
  const [generating, setGenerating] = useState('')

  const [businessMonth, setBusinessMonth] = useState(new Date().getMonth() + 1)
  const [companyCardMonth, setCompanyCardMonth] = useState(new Date().getMonth() + 1)
  const [reimbursableMonth, setReimbursableMonth] = useState(new Date().getMonth() + 1)
  const [personalMonth, setPersonalMonth] = useState(new Date().getMonth() + 1)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll() }, [year])

  const loadAll = async () => {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }
    setUserEmail(user.email || '')

    const [
      { data: profile },
      { data: tagsData },
      { data: pmData },
    ] = await Promise.all([
      supabase.from('profiles').select('full_name').eq('id', user.id).single(),
      supabase.from('tags').select('*').eq('user_id', user.id).order('sort_order'),
      supabase.from('payment_methods').select('*').eq('user_id', user.id).order('sort_order'),
    ])
    if (profile) setUserName(profile.full_name || user.email?.split('@')[0] || '')
    setTags(tagsData || [])
    setPaymentMethods(pmData || [])

    const [{ data: allTx }, { data: allTrips }] = await Promise.all([
      supabase.from('transactions').select('transaction_date').eq('user_id', user.id),
      supabase.from('mileage_trips').select('trip_date').eq('user_id', user.id),
    ])
    const yrs = new Set([new Date().getFullYear()])
    ;(allTx || []).forEach(r => { if (r.transaction_date) yrs.add(parseInt(r.transaction_date.slice(0, 4))) })
    ;(allTrips || []).forEach(tr => { if (tr.trip_date) yrs.add(parseInt(tr.trip_date.slice(0, 4))) })
    setAvailableYears(Array.from(yrs).sort().reverse())

    const yearStart = `${year}-01-01`
    const yearEnd = `${year}-12-31`
    const [{ data: yearReceipts }, { data: yearTrips }] = await Promise.all([
      supabase.from('transactions').select('*, categories(name, icon)').eq('user_id', user.id)
        .gte('transaction_date', yearStart).lte('transaction_date', yearEnd).order('transaction_date'),
      supabase.from('mileage_trips').select('*').eq('user_id', user.id)
        .gte('trip_date', yearStart).lte('trip_date', yearEnd).order('trip_date'),
    ])
    setReceipts(yearReceipts || [])
    setTrips(yearTrips || [])
    setLoading(false)
  }

  // Locale-aware formatters
  const lang = i18n.language || 'en'
  const locale = lang.startsWith('es') ? 'es-US' : 'en-US'
  const fmtMoney = (n) => new Intl.NumberFormat(locale, {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n || 0)
  const fmtMiles = (n) => (n || 0).toLocaleString(locale, { maximumFractionDigits: 1 })
  const fmtDateShort = (d) => new Date(d + 'T00:00:00').toLocaleDateString(locale, { month: '2-digit', day: '2-digit', year: '2-digit' })
  const fmtDateLong = (d) => new Date(d + 'T00:00:00').toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' })
  const monthName = (m) => new Date(2000, m - 1, 1).toLocaleDateString(locale, { month: 'long' })
  const monthSlug = (m) => new Date(2000, m - 1, 1).toLocaleDateString('en-US', { month: 'long' }).toLowerCase()

  const receiptsByTagType = (tagType) => {
    const relevantTagIds = tags.filter(tg => tg.tag_type === tagType).map(tg => tg.id)
    return receipts.filter(r => relevantTagIds.includes(r.tag_id))
  }

  const groupByCategory = (rs) => {
    const by = {}
    rs.forEach(r => {
      const name = r.categories?.name || t('reports.uncategorized')
      const icon = r.categories?.icon || ''
      if (!by[name]) by[name] = { total: 0, count: 0, icon }
      by[name].total += parseFloat(r.amount || 0)
      by[name].count += 1
    })
    return Object.entries(by)
      .sort((a, b) => b[1].total - a[1].total)
      .map(([name, d]) => ({ name, ...d }))
  }

  const groupByPaymentMethod = (rs) => {
    const by = {}
    rs.forEach(r => {
      const pm = paymentMethods.find(p => p.id === r.payment_method_id)
      const label = pm
        ? `${pm.label}${pm.last_4 ? ` ••${pm.last_4}` : ''}`
        : t('reports.no_payment_method')
      if (!by[label]) by[label] = { total: 0, count: 0, receipts: [], pm }
      by[label].total += parseFloat(r.amount || 0)
      by[label].count += 1
      by[label].receipts.push(r)
    })
    return Object.entries(by)
      .sort((a, b) => b[1].total - a[1].total)
      .map(([label, d]) => ({ label, ...d }))
  }

  const addHeader = (doc, title, accent = TAG_ACCENTS.schedule_c) => {
    const pageW = doc.internal.pageSize.getWidth()
    doc.setFillColor(accent.rgb[0], accent.rgb[1], accent.rgb[2])
    doc.rect(0, 0, pageW, 6, 'F')
    doc.setFontSize(20)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(30, 16, 64)
    doc.text('Merq Log', 40, 38)
    doc.setFontSize(11)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(107, 114, 128)
    doc.text(title, 40, 55)
  }

  // Stamps the footer on the CURRENT page. Page number passed in explicitly
  // (do NOT use getNumberOfPages here — it returns total pages, not current).
  const addFooter = (doc, footerNote = '', pageNum = 1) => {
    const pageW = doc.internal.pageSize.getWidth()
    const pageH = doc.internal.pageSize.getHeight()
    doc.setFontSize(8)
    doc.setTextColor(156, 163, 175)
    doc.setFont('helvetica', 'normal')
    const text = footerNote
      ? `${t('reports.footer_generated_by')} · merqlog.com · ${footerNote}`
      : `${t('reports.footer_generated_by')} · merqlog.com`
    doc.text(text, pageW / 2, pageH - 20, { align: 'center' })
    doc.text(`${t('reports.page')} ${pageNum}`, pageW - 40, pageH - 20, { align: 'right' })
  }

  // Stamps the footer on every page of the document exactly once.
  // Call this AFTER all content is rendered, just before doc.save().
  const finalizeFooters = (doc, footerNote = '') => {
    const total = doc.internal.getNumberOfPages()
    for (let i = 1; i <= total; i++) {
      doc.setPage(i)
      addFooter(doc, footerNote, i)
    }
  }

  const addHeroBox = (doc, label, amount, accent = TAG_ACCENTS.schedule_c, y = 80) => {
    const pageW = doc.internal.pageSize.getWidth()
    const margin = 40
    doc.setFillColor(accent.rgb[0], accent.rgb[1], accent.rgb[2])
    doc.roundedRect(margin, y, pageW - margin * 2, 80, 12, 12, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(11)
    doc.setFont('helvetica', 'normal')
    doc.text(label, margin + 20, y + 25)
    doc.setFontSize(32)
    doc.setFont('helvetica', 'bold')
    doc.text(fmtMoney(amount), margin + 20, y + 60)
  }

  const loadImageAsDataUrl = (url) => new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        const maxW = 1600
        const scale = Math.min(1, maxW / img.naturalWidth)
        canvas.width = Math.round(img.naturalWidth * scale)
        canvas.height = Math.round(img.naturalHeight * scale)
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        resolve({ dataUrl: canvas.toDataURL('image/jpeg', 0.82), w: canvas.width, h: canvas.height })
      } catch (err) { reject(err) }
    }
    img.onerror = () => reject(new Error(`Failed to load ${url}`))
    img.src = url
  })

  // Renders one attachment per receipt that has a file, in table order. Image
  // receipts are drawn straight into the jsPDF doc. jsPDF can't embed external
  // PDFs, so for PDF receipts we only record where their pages belong; they get
  // merged in later with pdf-lib (see saveReportWithAttachments). Returns the
  // list of pending PDF inserts.
  const appendReceiptAttachments = async (doc, rs, sectionTitle, accent) => {
    const pdfInserts = []
    const withFiles = rs.filter(r => r.receipt_url || r.receipt_file_path)
    if (withFiles.length === 0) return pdfInserts
    const pageW = doc.internal.pageSize.getWidth()
    const pageH = doc.internal.pageSize.getHeight()
    const margin = 40

    for (const r of withFiles) {
      const isPdf = r.receipt_file_type === 'pdf' || r.receipt_mime_type === 'application/pdf'
      const isImage = r.receipt_file_type === 'image' || (r.receipt_mime_type || '').startsWith('image/')

      if (isPdf) {
        // Defer — copy this PDF's pages in right after the current jsPDF page.
        pdfInserts.push({ insertAfter: doc.internal.getNumberOfPages(), receipt: r })
        continue
      }
      if (!isImage) continue // unknown file type — skip rather than render a broken page

      doc.addPage()
      addHeader(doc, sectionTitle, accent)
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(30, 16, 64)
      doc.text(`${fmtDateShort(r.transaction_date)} · ${r.merchant || t('reports.unknown')}`, margin, 88)
      doc.setFontSize(11)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(107, 114, 128)
      const catLabel = r.categories?.name ? `${r.categories.name} · ` : ''
      doc.text(`${catLabel}${fmtMoney(r.amount)}`, margin, 105)

      try {
        const { dataUrl, w, h } = await loadImageAsDataUrl(r.receipt_url)
        // Reserve a fixed band at the bottom for the footer so the image can
        // never render on top of it; scale the image down to fit if needed.
        const yPos = 120
        const footerReserve = 60
        const available = { w: pageW - margin * 2, h: pageH - yPos - footerReserve }
        const ratio = Math.min(available.w / w, available.h / h)
        const drawW = w * ratio
        const drawH = h * ratio
        const x = margin + (available.w - drawW) / 2
        doc.addImage(dataUrl, 'JPEG', x, yPos, drawW, drawH)
      } catch (err) {
        console.error('Receipt image load failed:', err)
        doc.setFontSize(10)
        doc.setTextColor(220, 38, 38)
        doc.text(t('reports.image_load_failed'), margin, 150)
      }
    }
    return pdfInserts
  }

  // Triggers a browser download for raw PDF bytes produced by pdf-lib.
  const downloadPdfBytes = (bytes, filename) => {
    const blob = new Blob([bytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // pdf-lib's standard fonts are WinAnsi-encoded and throw on glyphs they can't
  // represent (emoji, CJK, smart quotes). Fall back to '?' so a stray character
  // in a merchant name never aborts the whole report.
  const winAnsi = (s) => String(s == null ? '' : s)
    .split('')
    .map(ch => (ch.charCodeAt(0) <= 0xff ? ch : '?'))
    .join('')

  // Stamps the date · merchant / category · amount strip on an embedded receipt
  // PDF page, mirroring the header drawn above image receipts.
  const stampReceiptStrip = (page, r, fonts) => {
    const { width, height } = page.getSize()
    const stripH = 42
    page.drawRectangle({ x: 0, y: height - stripH, width, height: stripH, color: rgb(1, 1, 1) })
    page.drawRectangle({ x: 0, y: height - 4, width, height: 4, color: fonts.accent })
    page.drawText(winAnsi(`${fmtDateShort(r.transaction_date)} · ${r.merchant || t('reports.unknown')}`), {
      x: 40, y: height - 20, size: 11, font: fonts.bold, color: rgb(0.118, 0.063, 0.251),
    })
    const catLabel = r.categories?.name ? `${r.categories.name} · ` : ''
    page.drawText(winAnsi(`${catLabel}${fmtMoney(r.amount)}`), {
      x: 40, y: height - 34, size: 9, font: fonts.regular, color: rgb(0.42, 0.447, 0.502),
    })
  }

  // Fetches a receipt's stored bytes — Storage path first, public URL fallback.
  const fetchReceiptBytes = async (r) => {
    if (r.receipt_file_path) {
      const { data, error } = await supabase.storage.from('receipts').download(r.receipt_file_path)
      if (error) throw error
      return new Uint8Array(await data.arrayBuffer())
    }
    if (r.receipt_url) {
      const res = await fetch(r.receipt_url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return new Uint8Array(await res.arrayBuffer())
    }
    throw new Error('Receipt has no stored file')
  }

  // Finalizes a report: stamps footers, and when there are PDF receipts, merges
  // their pages into the report (in table order) with pdf-lib before saving.
  const saveReportWithAttachments = async (doc, pdfInserts, { accent, footerNote, filename }) => {
    finalizeFooters(doc, footerNote)

    // No PDF receipts → keep the original jsPDF-only output path (no regression).
    if (!pdfInserts || pdfInserts.length === 0) {
      doc.save(filename)
      return
    }

    const out = await PDFDocument.load(doc.output('arraybuffer'))
    const fonts = {
      regular: await out.embedFont(StandardFonts.Helvetica),
      bold: await out.embedFont(StandardFonts.HelveticaBold),
      accent: rgb(accent.rgb[0] / 255, accent.rgb[1] / 255, accent.rgb[2] / 255),
    }

    // Sorted by target position so the running offset stays correct as we insert.
    const inserts = [...pdfInserts].sort((a, b) => a.insertAfter - b.insertAfter)
    let offset = 0
    for (const ins of inserts) {
      const baseIndex = ins.insertAfter + offset
      try {
        const bytes = await fetchReceiptBytes(ins.receipt)
        const src = await PDFDocument.load(bytes, { ignoreEncryption: true })
        const pages = await out.copyPages(src, src.getPageIndices())
        pages.forEach((p, k) => {
          out.insertPage(baseIndex + k, p)
          try { stampReceiptStrip(p, ins.receipt, fonts) } catch (e) { /* strip is best-effort */ }
        })
        offset += pages.length
      } catch (err) {
        console.error('PDF receipt embed failed:', err)
        const ph = out.insertPage(baseIndex, [612, 792])
        try { stampReceiptStrip(ph, ins.receipt, fonts) } catch (e) { /* ignore */ }
        ph.drawText(winAnsi(t('reports.pdf_receipt_load_failed', {
          merchant: ins.receipt.merchant || t('reports.unknown'),
          amount: fmtMoney(ins.receipt.amount),
        })), { x: 40, y: 700, size: 11, font: fonts.regular, color: rgb(0.86, 0.15, 0.15) })
        offset += 1
      }
    }

    downloadPdfBytes(await out.save(), filename)
  }

  // ========== PDF: SCHEDULE C — FULL YEAR ==========
  const generateScheduleCFullYear = async () => {
    setGenerating('sc-full')
    try {
      const schC = receiptsByTagType('schedule_c')
      const rcptTotal = schC.reduce((s, r) => s + parseFloat(r.amount || 0), 0)
      const mileTotal = trips.reduce((s, tr) => s + parseFloat(tr.deduction_amount || 0), 0)
      const miles = trips.reduce((s, tr) => s + parseFloat(tr.miles || 0), 0)
      const grand = rcptTotal + mileTotal

      const doc = new jsPDF({ unit: 'pt', format: 'letter' })
      const pageW = doc.internal.pageSize.getWidth()
      const margin = 40
      const accent = TAG_ACCENTS.schedule_c

      addHeader(doc, t('reports.pdf_sc_full_title', { year }), accent)

      doc.setFontSize(10)
      doc.setTextColor(107, 114, 128)
      doc.text(t('reports.pdf_prepared_for'), margin, 85)
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(30, 16, 64)
      doc.text(userName || t('reports.business_owner_default'), margin, 103)

      doc.setFontSize(10)
      doc.setTextColor(107, 114, 128)
      doc.setFont('helvetica', 'normal')
      doc.text(t('reports.pdf_tax_year'), margin + 280, 85)
      doc.setFontSize(14)
      doc.setTextColor(30, 16, 64)
      doc.setFont('helvetica', 'bold')
      doc.text(String(year), margin + 280, 103)

      doc.setFillColor(accent.rgb[0], accent.rgb[1], accent.rgb[2])
      doc.roundedRect(margin, 128, pageW - margin * 2, 80, 12, 12, 'F')
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(11)
      doc.setFont('helvetica', 'normal')
      doc.text(t('reports.pdf_total_deduction_label'), margin + 20, 153)
      doc.setFontSize(32)
      doc.setFont('helvetica', 'bold')
      doc.text(fmtMoney(grand), margin + 20, 188)

      let y = 240
      doc.setTextColor(30, 16, 64)
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.text(t('reports.pdf_at_a_glance'), margin, y)
      y += 18
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(55, 65, 81)
      ;[
        t('reports.pdf_business_receipts_line', { count: schC.length, total: fmtMoney(rcptTotal) }),
        t('reports.pdf_mileage_line', { miles: fmtMiles(miles), deduction: fmtMoney(mileTotal) }),
        t('reports.pdf_trips_logged_line', { count: trips.length }),
        t('reports.pdf_date_range_line', { year }),
      ].forEach(line => { doc.text(line, margin, y); y += 16 })

      const catRows = groupByCategory(schC)
      if (mileTotal > 0) {
        catRows.push({ name: t('reports.pdf_mileage_irs_rate_row'), total: mileTotal, count: trips.length, icon: '' })
      }
      y += 10
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(30, 16, 64)
      doc.text(t('reports.pdf_by_category'), margin, y)

      const cpaFooter = t('reports.pdf_footer_cpa')
      if (catRows.length === 0) {
        y += 24
        doc.setFontSize(10)
        doc.setTextColor(156, 163, 175)
        doc.setFont('helvetica', 'italic')
        doc.text(t('reports.pdf_no_sc_data', { year }), margin, y)
      } else {
        autoTable(doc, {
          startY: y + 6,
          margin: { left: margin, right: margin },
          head: [[t('reports.col_category'), t('reports.col_count'), t('reports.col_total')]],
          body: catRows.map(r => [r.name, String(r.count), fmtMoney(r.total)]),
          theme: 'plain',
          styles: { fontSize: 9, cellPadding: { top: 8, right: 10, bottom: 8, left: 10 }, textColor: [30, 16, 64] },
          headStyles: { fillColor: [243, 244, 246], textColor: [107, 114, 128], fontStyle: 'bold', fontSize: 9 },
          columnStyles: {
            0: { cellWidth: 'auto' },
            1: { cellWidth: 60, halign: 'right' },
            2: { cellWidth: 90, halign: 'right', fontStyle: 'bold' },
          },
        })

        const byCard = groupByPaymentMethod(schC)
        if (byCard.length > 1 || (byCard.length === 1 && byCard[0].label !== t('reports.no_payment_method'))) {
          let y2 = doc.lastAutoTable.finalY + 24
          if (y2 > 680) { doc.addPage(); y2 = 60 }
          doc.setFontSize(13)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(30, 16, 64)
          doc.text(t('reports.pdf_by_payment_method'), margin, y2)
          autoTable(doc, {
            startY: y2 + 6,
            margin: { left: margin, right: margin },
            head: [[t('reports.col_card'), t('reports.col_count'), t('reports.col_total')]],
            body: byCard.map(c => [c.label, String(c.count), fmtMoney(c.total)]),
            theme: 'plain',
            styles: { fontSize: 9, cellPadding: { top: 8, right: 10, bottom: 8, left: 10 }, textColor: [30, 16, 64] },
            headStyles: { fillColor: [243, 244, 246], textColor: [107, 114, 128], fontStyle: 'bold', fontSize: 9 },
            columnStyles: {
              0: { cellWidth: 'auto' },
              1: { cellWidth: 60, halign: 'right' },
              2: { cellWidth: 90, halign: 'right', fontStyle: 'bold' },
            },
          })
        }
      }

      if (schC.length > 0) {
        doc.addPage()
        addHeader(doc, t('reports.pdf_receipts_year', { year }), accent)
        autoTable(doc, {
          startY: 85,
          margin: { left: margin, right: margin },
          head: [[t('reports.col_date'), t('reports.col_merchant'), t('reports.col_category'), t('reports.col_paid_with'), t('reports.col_amount')]],
          body: schC.map(r => {
            const pm = paymentMethods.find(p => p.id === r.payment_method_id)
            const pmLabel = pm ? `${pm.label}${pm.last_4 ? ` ••${pm.last_4}` : ''}` : '—'
            return [
              fmtDateShort(r.transaction_date),
              r.merchant || '—',
              r.categories?.name || '—',
              pmLabel,
              fmtMoney(r.amount),
            ]
          }),
          foot: [['', '', '', t('reports.col_total'), fmtMoney(rcptTotal)]],
          theme: 'plain',
          styles: { fontSize: 9, cellPadding: { top: 6, right: 8, bottom: 6, left: 8 }, textColor: [30, 16, 64] },
          headStyles: { fillColor: [243, 244, 246], textColor: [107, 114, 128], fontStyle: 'bold', fontSize: 9 },
          footStyles: { fillColor: [249, 250, 251], textColor: [30, 16, 64], fontStyle: 'bold', fontSize: 10 },
          columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 100 },
            3: { cellWidth: 110 },
            4: { cellWidth: 75, halign: 'right' },
          },
        })
      }

      if (trips.length > 0) {
        doc.addPage()
        addHeader(doc, t('reports.pdf_mileage_year', { year }), accent)
        autoTable(doc, {
          startY: 85,
          margin: { left: margin, right: margin },
          head: [[t('reports.col_date'), t('reports.col_from'), t('reports.col_to'), t('reports.col_miles'), t('reports.col_purpose'), t('reports.col_deduction')]],
          body: trips.map(tr => [
            fmtDateShort(tr.trip_date),
            (tr.from_address || '').split(',')[0],
            (tr.to_address || '').split(',')[0],
            fmtMiles(tr.miles) + (tr.round_trip ? ` ${t('reports.round_trip_short')}` : ''),
            tr.purpose || '—',
            fmtMoney(tr.deduction_amount),
          ]),
          foot: [['', '', '', fmtMiles(miles), t('reports.col_total'), fmtMoney(mileTotal)]],
          theme: 'plain',
          styles: { fontSize: 9, cellPadding: { top: 6, right: 8, bottom: 6, left: 8 }, textColor: [30, 16, 64] },
          headStyles: { fillColor: [243, 244, 246], textColor: [107, 114, 128], fontStyle: 'bold', fontSize: 9 },
          footStyles: { fillColor: [249, 250, 251], textColor: [30, 16, 64], fontStyle: 'bold', fontSize: 10 },
          columnStyles: {
            0: { cellWidth: 55 },
            1: { cellWidth: 100 },
            2: { cellWidth: 100 },
            3: { cellWidth: 55, halign: 'right' },
            4: { cellWidth: 'auto' },
            5: { cellWidth: 75, halign: 'right' },
          },
        })
      }

      finalizeFooters(doc, cpaFooter)

      doc.save(`merq-log-${year}-schedule-c-full-year.pdf`)
    } catch (err) { console.error(err); alert(t('reports.error_pdf_generic') + ': ' + err.message) }
    setGenerating('')
  }

  // ========== PDF: SCHEDULE C — MONTHLY ==========
  const generateScheduleCMonthly = async () => {
    setGenerating('sc-month')
    try {
      const m = businessMonth
      const monthStart = `${year}-${String(m).padStart(2, '0')}-01`
      const monthEnd = new Date(year, m, 0).toISOString().slice(0, 10)
      const schC = receiptsByTagType('schedule_c').filter(r => r.transaction_date >= monthStart && r.transaction_date <= monthEnd)
      const mTrips = trips.filter(tr => tr.trip_date >= monthStart && tr.trip_date <= monthEnd)
      const rTotal = schC.reduce((s, r) => s + parseFloat(r.amount || 0), 0)
      const mTotal = mTrips.reduce((s, tr) => s + parseFloat(tr.deduction_amount || 0), 0)
      const miles = mTrips.reduce((s, tr) => s + parseFloat(tr.miles || 0), 0)
      const grand = rTotal + mTotal

      const doc = new jsPDF({ unit: 'pt', format: 'letter' })
      const margin = 40
      const accent = TAG_ACCENTS.schedule_c
      const cpaFooter = t('reports.pdf_footer_cpa')

      addHeader(doc, t('reports.pdf_sc_monthly_title', { month: monthName(m), year }), accent)
      addHeroBox(doc, t('reports.pdf_deduction_this_month'), grand, accent)

      let y = 200
      doc.setTextColor(30, 16, 64)
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.text(t('reports.pdf_at_a_glance'), margin, y)
      y += 18
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(55, 65, 81)
      ;[
        t('reports.pdf_business_receipts_line', { count: schC.length, total: fmtMoney(rTotal) }),
        t('reports.pdf_mileage_line', { miles: fmtMiles(miles), deduction: fmtMoney(mTotal) }),
        t('reports.pdf_trips_short_line', { count: mTrips.length }),
      ].forEach(line => { doc.text(line, margin, y); y += 16 })

      if (schC.length > 0) {
        y += 14
        doc.setTextColor(30, 16, 64)
        doc.setFontSize(13)
        doc.setFont('helvetica', 'bold')
        doc.text(t('reports.pdf_receipts'), margin, y)
        autoTable(doc, {
          startY: y + 6,
          margin: { left: margin, right: margin },
          head: [[t('reports.col_date'), t('reports.col_merchant'), t('reports.col_category'), t('reports.col_amount')]],
          body: schC.map(r => [
            fmtDateShort(r.transaction_date),
            r.merchant || '—',
            r.categories?.name || '—',
            fmtMoney(r.amount),
          ]),
          foot: [['', '', t('reports.col_total'), fmtMoney(rTotal)]],
          theme: 'plain',
          styles: { fontSize: 9, cellPadding: { top: 6, right: 8, bottom: 6, left: 8 }, textColor: [30, 16, 64] },
          headStyles: { fillColor: [243, 244, 246], textColor: [107, 114, 128], fontStyle: 'bold', fontSize: 9 },
          footStyles: { fillColor: [249, 250, 251], fontStyle: 'bold', textColor: [30, 16, 64] },
          columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 110 },
            3: { cellWidth: 85, halign: 'right' },
          },
        })
        y = doc.lastAutoTable.finalY + 20
      }

      if (mTrips.length > 0) {
        if (y > 650) { doc.addPage(); y = 60 }
        doc.setTextColor(30, 16, 64)
        doc.setFontSize(13)
        doc.setFont('helvetica', 'bold')
        doc.text(t('reports.pdf_mileage'), margin, y)
        autoTable(doc, {
          startY: y + 6,
          margin: { left: margin, right: margin },
          head: [[t('reports.col_date'), t('reports.col_from'), t('reports.col_to'), t('reports.col_miles'), t('reports.col_deduction')]],
          body: mTrips.map(tr => [
            fmtDateShort(tr.trip_date),
            (tr.from_address || '').split(',')[0],
            (tr.to_address || '').split(',')[0],
            fmtMiles(tr.miles) + (tr.round_trip ? ` ${t('reports.round_trip_short')}` : ''),
            fmtMoney(tr.deduction_amount),
          ]),
          foot: [['', '', '', fmtMiles(miles), fmtMoney(mTotal)]],
          theme: 'plain',
          styles: { fontSize: 9, cellPadding: { top: 6, right: 8, bottom: 6, left: 8 }, textColor: [30, 16, 64] },
          headStyles: { fillColor: [243, 244, 246], textColor: [107, 114, 128], fontStyle: 'bold', fontSize: 9 },
          footStyles: { fillColor: [249, 250, 251], fontStyle: 'bold', textColor: [30, 16, 64] },
          columnStyles: {
            0: { cellWidth: 55 },
            1: { cellWidth: 140 },
            2: { cellWidth: 140 },
            3: { cellWidth: 65, halign: 'right' },
            4: { cellWidth: 85, halign: 'right' },
          },
        })
      }

      const pdfInserts = await appendReceiptAttachments(doc, schC, t('reports.pdf_receipt_images_title', { month: monthName(m), year }), accent)

      await saveReportWithAttachments(doc, pdfInserts, {
        accent, footerNote: cpaFooter, filename: `merq-log-${year}-${monthSlug(m)}-schedule-c.pdf`,
      })
    } catch (err) { console.error(err); alert(t('reports.error_pdf_generic') + ': ' + err.message) }
    setGenerating('')
  }

  // ========== PDF: COMPANY CARD ATTESTATION ==========
  const generateCompanyCardAttestation = async (specificPaymentMethodId = null) => {
    const genKey = specificPaymentMethodId ? `cc-${specificPaymentMethodId}` : 'cc-all'
    setGenerating(genKey)
    try {
      const m = companyCardMonth
      const monthStart = `${year}-${String(m).padStart(2, '0')}-01`
      const monthEnd = new Date(year, m, 0).toISOString().slice(0, 10)

      let monthReceipts = receiptsByTagType('company_card')
        .filter(r => r.transaction_date >= monthStart && r.transaction_date <= monthEnd)

      if (specificPaymentMethodId) {
        monthReceipts = monthReceipts.filter(r => r.payment_method_id === specificPaymentMethodId)
      }

      const total = monthReceipts.reduce((s, r) => s + parseFloat(r.amount || 0), 0)
      const specificPm = specificPaymentMethodId ? paymentMethods.find(p => p.id === specificPaymentMethodId) : null

      const doc = new jsPDF({ unit: 'pt', format: 'letter' })
      const pageW = doc.internal.pageSize.getWidth()
      const margin = 40
      const accent = TAG_ACCENTS.company_card
      const ccFooter = t('reports.pdf_footer_cc')

      const pdfTitle = specificPm
        ? t('reports.pdf_cc_specific_title', { card: specificPm.label, last4: specificPm.last_4 ? ` ••${specificPm.last_4}` : '', month: monthName(m), year })
        : t('reports.pdf_cc_all_title', { month: monthName(m), year })
      addHeader(doc, pdfTitle, accent)

      doc.setFontSize(10)
      doc.setTextColor(107, 114, 128)
      doc.text(t('reports.pdf_employee'), margin, 85)
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(30, 16, 64)
      doc.text(userName || userEmail || t('reports.employee_default'), margin, 103)
      doc.setFontSize(10)
      doc.setTextColor(107, 114, 128)
      doc.setFont('helvetica', 'normal')
      doc.text(userEmail, margin, 118)

      doc.setFontSize(10)
      doc.setTextColor(107, 114, 128)
      doc.text(t('reports.pdf_period'), margin + 280, 85)
      doc.setFontSize(13)
      doc.setTextColor(30, 16, 64)
      doc.setFont('helvetica', 'bold')
      doc.text(`${monthName(m)} ${year}`, margin + 280, 103)
      doc.setFontSize(10)
      doc.setTextColor(107, 114, 128)
      doc.setFont('helvetica', 'normal')
      doc.text(t('reports.pdf_submitted_date', { date: fmtDateLong(new Date().toISOString().slice(0, 10)) }), margin + 280, 118)

      doc.setFillColor(accent.rgb[0], accent.rgb[1], accent.rgb[2])
      doc.roundedRect(margin, 140, pageW - margin * 2, 80, 12, 12, 'F')
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(11)
      doc.setFont('helvetica', 'normal')
      doc.text(t('reports.pdf_total_charges_period'), margin + 20, 165)
      doc.setFontSize(32)
      doc.setFont('helvetica', 'bold')
      doc.text(fmtMoney(total), margin + 20, 200)

      if (monthReceipts.length === 0) {
        doc.setFontSize(11)
        doc.setTextColor(107, 114, 128)
        doc.setFont('helvetica', 'italic')
        doc.text(t('reports.pdf_no_cc_period'), margin, 260)

        finalizeFooters(doc, ccFooter)

        doc.save(`merq-log-${year}-${monthSlug(m)}-company-card-attestation.pdf`)
        setGenerating('')
        return
      }

      const byCard = groupByPaymentMethod(monthReceipts)

      let y = 250
      doc.setTextColor(30, 16, 64)
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.text(t('reports.pdf_by_card'), margin, y)

      autoTable(doc, {
        startY: y + 6,
        margin: { left: margin, right: margin },
        head: [[t('reports.col_card'), t('reports.col_charges'), t('reports.col_total')]],
        body: byCard.map(c => [c.label, String(c.count), fmtMoney(c.total)]),
        foot: [[t('reports.col_total'), String(monthReceipts.length), fmtMoney(total)]],
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: { top: 8, right: 10, bottom: 8, left: 10 }, textColor: [30, 16, 64] },
        headStyles: { fillColor: [243, 244, 246], textColor: [107, 114, 128], fontStyle: 'bold', fontSize: 9 },
        footStyles: { fillColor: [249, 250, 251], fontStyle: 'bold', textColor: [30, 16, 64] },
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 80, halign: 'right' },
          2: { cellWidth: 100, halign: 'right', fontStyle: 'bold' },
        },
      })

      doc.addPage()
      addHeader(doc, pdfTitle, accent)
      autoTable(doc, {
        startY: 85,
        margin: { left: margin, right: margin },
        head: [[t('reports.col_date'), t('reports.col_merchant'), t('reports.col_category'), t('reports.col_card'), t('reports.col_amount')]],
        body: monthReceipts.map(r => {
          const pm = paymentMethods.find(p => p.id === r.payment_method_id)
          const cardLabel = pm ? `${pm.label}${pm.last_4 ? ` ••${pm.last_4}` : ''}` : '—'
          return [
            fmtDateShort(r.transaction_date),
            r.merchant || '—',
            r.categories?.name || '—',
            cardLabel,
            fmtMoney(r.amount),
          ]
        }),
        foot: [['', '', '', t('reports.col_total'), fmtMoney(total)]],
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: { top: 6, right: 8, bottom: 6, left: 8 }, textColor: [30, 16, 64] },
        headStyles: { fillColor: [243, 244, 246], textColor: [107, 114, 128], fontStyle: 'bold', fontSize: 9 },
        footStyles: { fillColor: [249, 250, 251], fontStyle: 'bold', textColor: [30, 16, 64] },
        columnStyles: {
          0: { cellWidth: 55 },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 90 },
          3: { cellWidth: 120 },
          4: { cellWidth: 75, halign: 'right' },
        },
      })

      const lastY = doc.lastAutoTable.finalY + 24
      doc.setFontSize(9)
      doc.setTextColor(107, 114, 128)
      doc.setFont('helvetica', 'italic')
      const attest = t('reports.pdf_cc_attestation', { month: monthName(m), year })
      const split = doc.splitTextToSize(attest, pageW - margin * 2)
      doc.text(split, margin, lastY)

      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(30, 16, 64)
      const signY = lastY + split.length * 12 + 30
      doc.text(t('reports.pdf_employee_signature'), margin, signY)
      doc.setDrawColor(156, 163, 175)
      doc.setLineWidth(0.5)
      doc.line(margin, signY + 20, margin + 240, signY + 20)
      doc.text(t('reports.pdf_date'), margin + 280, signY)
      doc.line(margin + 280, signY + 20, margin + 440, signY + 20)

      const pdfInserts = await appendReceiptAttachments(doc, monthReceipts, pdfTitle, accent)

      const filenameSuffix = specificPm
        ? `-${specificPm.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}${specificPm.last_4 ? `-${specificPm.last_4}` : ''}`
        : '-all-cards'
      await saveReportWithAttachments(doc, pdfInserts, {
        accent, footerNote: ccFooter,
        filename: `merq-log-${year}-${monthSlug(m)}-company-card-attestation${filenameSuffix}.pdf`,
      })
    } catch (err) { console.error(err); alert(t('reports.error_pdf_generic') + ': ' + err.message) }
    setGenerating('')
  }

  // ========== PDF: REIMBURSEMENT ==========
  const generateReimbursementRequest = async () => {
    setGenerating('reimb')
    try {
      const m = reimbursableMonth
      const monthStart = `${year}-${String(m).padStart(2, '0')}-01`
      const monthEnd = new Date(year, m, 0).toISOString().slice(0, 10)
      const monthReceipts = receiptsByTagType('reimbursable')
        .filter(r => r.transaction_date >= monthStart && r.transaction_date <= monthEnd)
      const total = monthReceipts.reduce((s, r) => s + parseFloat(r.amount || 0), 0)

      const doc = new jsPDF({ unit: 'pt', format: 'letter' })
      const pageW = doc.internal.pageSize.getWidth()
      const margin = 40
      const accent = TAG_ACCENTS.reimbursable
      const reimbFooter = t('reports.pdf_footer_reimb')

      addHeader(doc, t('reports.pdf_reimb_title', { month: monthName(m), year }), accent)

      doc.setFontSize(10)
      doc.setTextColor(107, 114, 128)
      doc.text(t('reports.pdf_submitted_by'), margin, 85)
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(30, 16, 64)
      doc.text(userName || userEmail || t('reports.employee_default'), margin, 103)
      doc.setFontSize(10)
      doc.setTextColor(107, 114, 128)
      doc.setFont('helvetica', 'normal')
      doc.text(userEmail, margin, 118)

      doc.setFontSize(10)
      doc.setTextColor(107, 114, 128)
      doc.text(t('reports.pdf_period'), margin + 280, 85)
      doc.setFontSize(13)
      doc.setTextColor(30, 16, 64)
      doc.setFont('helvetica', 'bold')
      doc.text(`${monthName(m)} ${year}`, margin + 280, 103)
      doc.setFontSize(10)
      doc.setTextColor(107, 114, 128)
      doc.setFont('helvetica', 'normal')
      doc.text(t('reports.pdf_submitted_date', { date: fmtDateLong(new Date().toISOString().slice(0, 10)) }), margin + 280, 118)

      doc.setFillColor(accent.rgb[0], accent.rgb[1], accent.rgb[2])
      doc.roundedRect(margin, 140, pageW - margin * 2, 80, 12, 12, 'F')
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(11)
      doc.setFont('helvetica', 'normal')
      doc.text(t('reports.pdf_total_owed'), margin + 20, 165)
      doc.setFontSize(32)
      doc.setFont('helvetica', 'bold')
      doc.text(fmtMoney(total), margin + 20, 200)

      if (monthReceipts.length === 0) {
        doc.setFontSize(11)
        doc.setTextColor(107, 114, 128)
        doc.setFont('helvetica', 'italic')
        doc.text(t('reports.pdf_no_reimb_period'), margin, 260)

        finalizeFooters(doc, reimbFooter)

        doc.save(`merq-log-${year}-${monthSlug(m)}-reimbursement-request.pdf`)
        setGenerating('')
        return
      }

      let y = 250
      doc.setTextColor(30, 16, 64)
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.text(t('reports.pdf_expenses'), margin, y)
      autoTable(doc, {
        startY: y + 6,
        margin: { left: margin, right: margin },
        head: [[t('reports.col_date'), t('reports.col_merchant'), t('reports.col_category'), t('reports.col_paid_with'), t('reports.col_amount')]],
        body: monthReceipts.map(r => {
          const pm = paymentMethods.find(p => p.id === r.payment_method_id)
          const pmLabel = pm ? `${pm.label}${pm.last_4 ? ` ••${pm.last_4}` : ''}` : '—'
          return [
            fmtDateShort(r.transaction_date),
            r.merchant || '—',
            r.categories?.name || '—',
            pmLabel,
            fmtMoney(r.amount),
          ]
        }),
        foot: [['', '', '', t('reports.col_total_owed'), fmtMoney(total)]],
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: { top: 6, right: 8, bottom: 6, left: 8 }, textColor: [30, 16, 64] },
        headStyles: { fillColor: [243, 244, 246], textColor: [107, 114, 128], fontStyle: 'bold', fontSize: 9 },
        footStyles: { fillColor: [249, 250, 251], fontStyle: 'bold', textColor: [30, 16, 64] },
        columnStyles: {
          0: { cellWidth: 55 },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 90 },
          3: { cellWidth: 110 },
          4: { cellWidth: 85, halign: 'right' },
        },
      })

      const lastY = doc.lastAutoTable.finalY + 24
      doc.setFontSize(9)
      doc.setTextColor(107, 114, 128)
      doc.setFont('helvetica', 'italic')
      const attest = t('reports.pdf_reimb_attestation', { month: monthName(m), year })
      const split = doc.splitTextToSize(attest, pageW - margin * 2)
      doc.text(split, margin, lastY)

      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(30, 16, 64)
      const signY = lastY + split.length * 12 + 30
      doc.text(t('reports.pdf_employee_signature'), margin, signY)
      doc.setDrawColor(156, 163, 175)
      doc.setLineWidth(0.5)
      doc.line(margin, signY + 20, margin + 240, signY + 20)
      doc.text(t('reports.pdf_date'), margin + 280, signY)
      doc.line(margin + 280, signY + 20, margin + 440, signY + 20)

      const pdfInserts = await appendReceiptAttachments(doc, monthReceipts, t('reports.pdf_reimb_title', { month: monthName(m), year }), accent)

      await saveReportWithAttachments(doc, pdfInserts, {
        accent, footerNote: reimbFooter, filename: `merq-log-${year}-${monthSlug(m)}-reimbursement-request.pdf`,
      })
    } catch (err) { console.error(err); alert(t('reports.error_pdf_generic') + ': ' + err.message) }
    setGenerating('')
  }

  // ========== PDF: PERSONAL FULL YEAR ==========
  const generatePersonalFullYear = async () => {
    setGenerating('pers-full')
    try {
      const pers = receiptsByTagType('personal')
      const total = pers.reduce((s, r) => s + parseFloat(r.amount || 0), 0)

      const doc = new jsPDF({ unit: 'pt', format: 'letter' })
      const margin = 40
      const accent = TAG_ACCENTS.personal
      const persFooter = t('reports.pdf_footer_pers')

      addHeader(doc, t('reports.pdf_pers_full_title', { year }), accent)
      addHeroBox(doc, t('reports.pdf_total_personal'), total, accent)

      let y = 200
      doc.setTextColor(30, 16, 64)
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.text(t('reports.pdf_at_a_glance'), margin, y)
      y += 18
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(55, 65, 81)
      ;[
        t('reports.pdf_receipts_saved', { count: pers.length }),
        t('reports.pdf_total_line', { total: fmtMoney(total) }),
        t('reports.pdf_date_range_line', { year }),
      ].forEach(line => { doc.text(line, margin, y); y += 16 })

      y += 10
      doc.setTextColor(30, 16, 64)
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.text(t('reports.pdf_by_category'), margin, y)

      const catRows = groupByCategory(pers)
      if (catRows.length === 0) {
        y += 24
        doc.setFontSize(10)
        doc.setTextColor(156, 163, 175)
        doc.setFont('helvetica', 'italic')
        doc.text(t('reports.pdf_no_pers_year', { year }), margin, y)
      } else {
        autoTable(doc, {
          startY: y + 6,
          margin: { left: margin, right: margin },
          head: [[t('reports.col_category'), t('reports.col_count'), t('reports.col_total')]],
          body: catRows.map(r => [r.name, String(r.count), fmtMoney(r.total)]),
          theme: 'plain',
          styles: { fontSize: 9, cellPadding: { top: 8, right: 10, bottom: 8, left: 10 }, textColor: [30, 16, 64] },
          headStyles: { fillColor: [243, 244, 246], textColor: [107, 114, 128], fontStyle: 'bold', fontSize: 9 },
          columnStyles: {
            0: { cellWidth: 'auto' },
            1: { cellWidth: 60, halign: 'right' },
            2: { cellWidth: 90, halign: 'right', fontStyle: 'bold' },
          },
        })

        const byCard = groupByPaymentMethod(pers)
        if (byCard.length > 1 || (byCard.length === 1 && byCard[0].label !== t('reports.no_payment_method'))) {
          let y2 = doc.lastAutoTable.finalY + 24
          if (y2 > 680) { doc.addPage(); y2 = 60 }
          doc.setFontSize(13)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(30, 16, 64)
          doc.text(t('reports.pdf_by_payment_method'), margin, y2)
          autoTable(doc, {
            startY: y2 + 6,
            margin: { left: margin, right: margin },
            head: [[t('reports.col_card'), t('reports.col_count'), t('reports.col_total')]],
            body: byCard.map(c => [c.label, String(c.count), fmtMoney(c.total)]),
            theme: 'plain',
            styles: { fontSize: 9, cellPadding: { top: 8, right: 10, bottom: 8, left: 10 }, textColor: [30, 16, 64] },
            headStyles: { fillColor: [243, 244, 246], textColor: [107, 114, 128], fontStyle: 'bold', fontSize: 9 },
            columnStyles: {
              0: { cellWidth: 'auto' },
              1: { cellWidth: 60, halign: 'right' },
              2: { cellWidth: 90, halign: 'right', fontStyle: 'bold' },
            },
          })
        }
      }

      if (pers.length > 0) {
        doc.addPage()
        addHeader(doc, t('reports.pdf_all_pers_year', { year }), accent)
        autoTable(doc, {
          startY: 85,
          margin: { left: margin, right: margin },
          head: [[t('reports.col_date'), t('reports.col_merchant'), t('reports.col_category'), t('reports.col_amount')]],
          body: pers.map(r => [
            fmtDateShort(r.transaction_date),
            r.merchant || '—',
            r.categories?.name || '—',
            fmtMoney(r.amount),
          ]),
          foot: [['', '', t('reports.col_total'), fmtMoney(total)]],
          theme: 'plain',
          styles: { fontSize: 9, cellPadding: { top: 6, right: 8, bottom: 6, left: 8 }, textColor: [30, 16, 64] },
          headStyles: { fillColor: [243, 244, 246], textColor: [107, 114, 128], fontStyle: 'bold', fontSize: 9 },
          footStyles: { fillColor: [249, 250, 251], textColor: [30, 16, 64], fontStyle: 'bold', fontSize: 10 },
          columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 100 },
            3: { cellWidth: 75, halign: 'right' },
          },
        })
      }

      finalizeFooters(doc, persFooter)

      doc.save(`merq-log-${year}-personal-spending-full-year.pdf`)
    } catch (err) { console.error(err); alert(t('reports.error_pdf_generic') + ': ' + err.message) }
    setGenerating('')
  }

  // ========== PDF: PERSONAL MONTHLY ==========
  const generatePersonalMonthly = async () => {
    setGenerating('pers-month')
    try {
      const m = personalMonth
      const monthStart = `${year}-${String(m).padStart(2, '0')}-01`
      const monthEnd = new Date(year, m, 0).toISOString().slice(0, 10)
      const monthReceipts = receiptsByTagType('personal')
        .filter(r => r.transaction_date >= monthStart && r.transaction_date <= monthEnd)
      const total = monthReceipts.reduce((s, r) => s + parseFloat(r.amount || 0), 0)

      const doc = new jsPDF({ unit: 'pt', format: 'letter' })
      const margin = 40
      const accent = TAG_ACCENTS.personal
      const persFooter = t('reports.pdf_footer_pers')

      addHeader(doc, t('reports.pdf_pers_monthly_title', { month: monthName(m), year }), accent)
      addHeroBox(doc, t('reports.pdf_spending_this_month'), total, accent)

      let y = 200
      doc.setTextColor(30, 16, 64)
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.text(t('reports.pdf_receipts'), margin, y)

      if (monthReceipts.length === 0) {
        y += 24
        doc.setFontSize(10)
        doc.setTextColor(156, 163, 175)
        doc.setFont('helvetica', 'italic')
        doc.text(t('reports.pdf_no_pers_month'), margin, y)
      } else {
        autoTable(doc, {
          startY: y + 6,
          margin: { left: margin, right: margin },
          head: [[t('reports.col_date'), t('reports.col_merchant'), t('reports.col_category'), t('reports.col_amount')]],
          body: monthReceipts.map(r => [
            fmtDateShort(r.transaction_date),
            r.merchant || '—',
            r.categories?.name || '—',
            fmtMoney(r.amount),
          ]),
          foot: [['', '', t('reports.col_total'), fmtMoney(total)]],
          theme: 'plain',
          styles: { fontSize: 9, cellPadding: { top: 6, right: 8, bottom: 6, left: 8 }, textColor: [30, 16, 64] },
          headStyles: { fillColor: [243, 244, 246], textColor: [107, 114, 128], fontStyle: 'bold', fontSize: 9 },
          footStyles: { fillColor: [249, 250, 251], fontStyle: 'bold', textColor: [30, 16, 64] },
          columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 110 },
            3: { cellWidth: 85, halign: 'right' },
          },
        })
      }

      const pdfInserts = await appendReceiptAttachments(doc, monthReceipts, t('reports.pdf_receipt_images_title', { month: monthName(m), year }), accent)

      await saveReportWithAttachments(doc, pdfInserts, {
        accent, footerNote: persFooter, filename: `merq-log-${year}-${monthSlug(m)}-personal-spending.pdf`,
      })
    } catch (err) { console.error(err); alert(t('reports.error_pdf_generic') + ': ' + err.message) }
    setGenerating('')
  }

  // ========== CSV ==========
  const downloadFile = (content, filename, mime) => {
    const blob = new Blob([content], { type: mime })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const escapeCsv = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const buildReceiptCsv = (rs) => {
    const headers = [
      t('reports.col_date'),
      t('reports.col_merchant'),
      t('reports.col_category'),
      t('reports.col_tag'),
      t('reports.col_paid_with'),
      t('reports.col_memo'),
      t('reports.col_amount'),
    ]
    const rows = rs.map(r => {
      const pm = paymentMethods.find(p => p.id === r.payment_method_id)
      const pmLabel = pm ? `${pm.label}${pm.last_4 ? ` ••${pm.last_4}` : ''}` : ''
      const tag = tags.find(tg => tg.id === r.tag_id)
      return [
        r.transaction_date || '',
        r.merchant || '',
        r.categories?.name || '',
        tag?.name || '',
        pmLabel,
        r.memo || '',
        r.amount || 0,
      ].map(escapeCsv).join(',')
    })
    return [headers.map(escapeCsv).join(','), ...rows].join('\n')
  }

  const downloadScheduleCCsv = () => {
    setGenerating('sc-csv')
    try {
      const schC = receiptsByTagType('schedule_c')
      downloadFile(buildReceiptCsv(schC), `merq-log-${year}-schedule-c-expenses.csv`, 'text/csv')

      if (trips.length > 0) {
        const headers = [
          t('reports.col_date'), t('reports.col_from'), t('reports.col_to'),
          t('reports.col_miles'), t('reports.col_round_trip'), t('reports.col_purpose'),
          t('reports.col_rate_cents'), t('reports.col_deduction'),
        ]
        const rows = trips.map(tr => [
          tr.trip_date || '', tr.from_address || '', tr.to_address || '',
          tr.miles || 0, tr.round_trip ? t('reports.yes') : t('reports.no'), tr.purpose || '',
          tr.rate_used_cents || 0, tr.deduction_amount || 0,
        ].map(escapeCsv).join(','))
        const csv = [headers.map(escapeCsv).join(','), ...rows].join('\n')
        setTimeout(() => downloadFile(csv, `merq-log-${year}-mileage.csv`, 'text/csv'), 400)
      }
    } catch (err) { console.error(err); alert(t('reports.error_csv') + ': ' + err.message) }
    setGenerating('')
  }

  const downloadCompanyCardCsv = () => {
    setGenerating('cc-csv')
    try {
      const cc = receiptsByTagType('company_card')
      downloadFile(buildReceiptCsv(cc), `merq-log-${year}-company-card-charges.csv`, 'text/csv')
    } catch (err) { alert(t('reports.error_csv') + ': ' + err.message) }
    setGenerating('')
  }

  const downloadReimbursableCsv = () => {
    setGenerating('reimb-csv')
    try {
      const rb = receiptsByTagType('reimbursable')
      downloadFile(buildReceiptCsv(rb), `merq-log-${year}-reimbursable.csv`, 'text/csv')
    } catch (err) { alert(t('reports.error_csv') + ': ' + err.message) }
    setGenerating('')
  }

  const downloadPersonalCsv = () => {
    setGenerating('pers-csv')
    try {
      const p = receiptsByTagType('personal')
      downloadFile(buildReceiptCsv(p), `merq-log-${year}-personal-spending.csv`, 'text/csv')
    } catch (err) { alert(t('reports.error_csv') + ': ' + err.message) }
    setGenerating('')
  }

  // ========== RENDER ==========
  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1)

  const schCReceipts = receiptsByTagType('schedule_c')
  const schCTotal = schCReceipts.reduce((s, r) => s + parseFloat(r.amount || 0), 0)
  const miles = trips.reduce((s, tr) => s + parseFloat(tr.miles || 0), 0)
  const mileTotal = trips.reduce((s, tr) => s + parseFloat(tr.deduction_amount || 0), 0)
  const schCDeduction = schCTotal + mileTotal

  const ccReceipts = receiptsByTagType('company_card')
  const ccTotal = ccReceipts.reduce((s, r) => s + parseFloat(r.amount || 0), 0)

  const rbReceipts = receiptsByTagType('reimbursable')
  const rbTotal = rbReceipts.reduce((s, r) => s + parseFloat(r.amount || 0), 0)

  const persReceipts = receiptsByTagType('personal')
  const persTotal = persReceipts.reduce((s, r) => s + parseFloat(r.amount || 0), 0)

  const ccPaymentMethods = (() => {
    const cardIds = new Set()
    ccReceipts.forEach(r => { if (r.payment_method_id) cardIds.add(r.payment_method_id) })
    return paymentMethods.filter(p => cardIds.has(p.id))
  })()

  const monthsWithData = (tagType) => {
    const s = new Set()
    receiptsByTagType(tagType).forEach(r => {
      if (r.transaction_date) s.add(parseInt(r.transaction_date.slice(5, 7)))
    })
    return s
  }

  const buildReceiptPreview = (rs) => ({
    total: rs.reduce((s, r) => s + parseFloat(r.amount || 0), 0),
    count: rs.length,
    items: rs.slice().sort((a, b) => (b.transaction_date || '').localeCompare(a.transaction_date || '')).map(r => ({
      date: r.transaction_date,
      label: r.merchant || t('reports.unknown'),
      amount: parseFloat(r.amount || 0),
    })),
  })

  const buildScheduleCFullYearPreview = () => {
    const totalDed = schCTotal + mileTotal
    const items = [
      ...schCReceipts.map(r => ({
        date: r.transaction_date,
        label: r.merchant || t('reports.unknown'),
        amount: parseFloat(r.amount || 0),
      })),
      ...trips.map(tr => ({
        date: tr.trip_date,
        label: t('reports.preview_mileage_label', {
          from: (tr.from_address || '').split(',')[0],
          to: (tr.to_address || '').split(',')[0],
        }),
        amount: parseFloat(tr.deduction_amount || 0),
      })),
    ].sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    return { total: totalDed, count: items.length, items }
  }

  const buildMonthFilter = (m) => {
    const monthStart = `${year}-${String(m).padStart(2, '0')}-01`
    const monthEnd = new Date(year, m, 0).toISOString().slice(0, 10)
    return (r) => {
      const d = r.transaction_date || r.trip_date
      return d && d >= monthStart && d <= monthEnd
    }
  }

  const buildScheduleCMonthlyPreview = () => {
    const filter = buildMonthFilter(businessMonth)
    const mR = schCReceipts.filter(filter)
    const mT = trips.filter(filter)
    const items = [
      ...mR.map(r => ({
        date: r.transaction_date,
        label: r.merchant || t('reports.unknown'),
        amount: parseFloat(r.amount || 0),
      })),
      ...mT.map(tr => ({
        date: tr.trip_date,
        label: t('reports.preview_mileage_label', {
          from: (tr.from_address || '').split(',')[0],
          to: (tr.to_address || '').split(',')[0],
        }),
        amount: parseFloat(tr.deduction_amount || 0),
      })),
    ].sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    const total = items.reduce((s, i) => s + i.amount, 0)
    return { total, count: items.length, items }
  }

  const buildCompanyCardMonthlyPreview = (pmId = null) => {
    const filter = buildMonthFilter(companyCardMonth)
    let rs = ccReceipts.filter(filter)
    if (pmId) rs = rs.filter(r => r.payment_method_id === pmId)
    return buildReceiptPreview(rs)
  }

  const buildReimbursablePreview = () => {
    const filter = buildMonthFilter(reimbursableMonth)
    return buildReceiptPreview(rbReceipts.filter(filter))
  }

  const buildPersonalMonthlyPreview = () => {
    const filter = buildMonthFilter(personalMonth)
    return buildReceiptPreview(persReceipts.filter(filter))
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>{t('reports.title')}</h1>
          <p style={styles.subtitle}>{t('reports.subtitle')}</p>
        </div>
      </div>

      <div style={styles.yearRow}>
        <span style={styles.yearLabel}>{t('reports.tax_year_label')}</span>
        <select style={styles.yearSelect} value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
          {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <div style={styles.previewCard}>
        <div style={styles.previewRow}>
          <PreviewStat label={t('reports.stat_schedule_c')} sub={t('reports.stat_sc_sub', { count: schCReceipts.length, miles: fmtMiles(miles) })} value={fmtMoney(schCDeduction)} valueColor={TAG_ACCENTS.schedule_c.hex} />
          <PreviewStat label={t('reports.stat_company_card')} sub={t('reports.stat_cc_sub', { count: ccReceipts.length })} value={fmtMoney(ccTotal)} valueColor={TAG_ACCENTS.company_card.hex} />
          <PreviewStat label={t('reports.stat_reimbursable')} sub={t('reports.stat_rb_sub', { count: rbReceipts.length })} value={fmtMoney(rbTotal)} valueColor={TAG_ACCENTS.reimbursable.hex} />
          <PreviewStat label={t('reports.stat_personal')} sub={t('reports.stat_pers_sub', { count: persReceipts.length })} value={fmtMoney(persTotal)} valueColor={TAG_ACCENTS.personal.hex} />
        </div>
      </div>

      {loading ? (
        <div style={styles.loading}>{t('common.loading')}</div>
      ) : (
        <>
          <SectionHeader icon="💼" title={t('reports.section_sc_title')} sub={t('reports.section_sc_sub')} accent={TAG_ACCENTS.schedule_c.hex} />
          <div style={styles.reportGrid}>
            <ReportCard t={t} fmtMoney={fmtMoney} icon="📄" title={t('reports.card_sc_full_title', { year })} body={t('reports.card_sc_full_body')} buttonLabel={t('reports.download_pdf')} disabled={(schCReceipts.length === 0 && trips.length === 0) || !!generating} loading={generating === 'sc-full'} variant="primary" previewData={buildScheduleCFullYearPreview()} onClick={generateScheduleCFullYear} />
            <ReportCard t={t} fmtMoney={fmtMoney} icon="📅" title={t('reports.card_sc_monthly_title')} body={t('reports.card_sc_monthly_body')} buttonLabel={t('reports.download_pdf')} disabled={(schCReceipts.length === 0 && trips.length === 0) || !!generating} loading={generating === 'sc-month'} monthPicker={{ value: businessMonth, onChange: setBusinessMonth, options: monthOptions, monthsWithData: monthsWithData('schedule_c') }} previewData={buildScheduleCMonthlyPreview()} onClick={generateScheduleCMonthly} />
            <ReportCard t={t} fmtMoney={fmtMoney} icon="📊" title={t('reports.card_sc_csv_title', { year })} body={t('reports.card_sc_csv_body')} buttonLabel={t('reports.download_csvs')} disabled={(schCReceipts.length === 0 && trips.length === 0) || !!generating} loading={generating === 'sc-csv'} previewData={buildScheduleCFullYearPreview()} onClick={downloadScheduleCCsv} />
          </div>

          <SectionHeader icon="🏢" title={t('reports.section_cc_title')} sub={t('reports.section_cc_sub')} accent={TAG_ACCENTS.company_card.hex} marginTop={40} />
          <div style={styles.reportGrid}>
            <ReportCard t={t} fmtMoney={fmtMoney} icon="📅" title={t('reports.card_cc_all_title')} body={t('reports.card_cc_all_body')} buttonLabel={t('reports.download_pdf')} disabled={ccReceipts.length === 0 || !!generating} loading={generating === 'cc-all'} variant="cc" monthPicker={{ value: companyCardMonth, onChange: setCompanyCardMonth, options: monthOptions, monthsWithData: monthsWithData('company_card') }} previewData={buildCompanyCardMonthlyPreview(null)} onClick={() => generateCompanyCardAttestation(null)} />
            {ccPaymentMethods.map(pm => (
              <ReportCard
                key={pm.id}
                t={t}
                fmtMoney={fmtMoney}
                icon="💳"
                title={t('reports.card_cc_specific_title', { card: pm.label, last4: pm.last_4 ? ` ••${pm.last_4}` : '' })}
                body={t('reports.card_cc_specific_body', { card: pm.label })}
                buttonLabel={t('reports.download_pdf')}
                disabled={!!generating}
                loading={generating === `cc-${pm.id}`}
                variant="cc"
                monthPicker={{ value: companyCardMonth, onChange: setCompanyCardMonth, options: monthOptions, monthsWithData: monthsWithData('company_card') }}
                previewData={buildCompanyCardMonthlyPreview(pm.id)}
                onClick={() => generateCompanyCardAttestation(pm.id)}
              />
            ))}
            <ReportCard t={t} fmtMoney={fmtMoney} icon="📊" title={t('reports.card_cc_csv_title', { year })} body={t('reports.card_cc_csv_body')} buttonLabel={t('reports.download_csv')} disabled={ccReceipts.length === 0 || !!generating} loading={generating === 'cc-csv'} previewData={buildReceiptPreview(ccReceipts)} onClick={downloadCompanyCardCsv} />
          </div>

          <SectionHeader icon="💵" title={t('reports.section_rb_title')} sub={t('reports.section_rb_sub')} accent={TAG_ACCENTS.reimbursable.hex} marginTop={40} />
          <div style={styles.reportGrid}>
            <ReportCard t={t} fmtMoney={fmtMoney} icon="📅" title={t('reports.card_rb_title')} body={t('reports.card_rb_body')} buttonLabel={t('reports.download_pdf')} disabled={rbReceipts.length === 0 || !!generating} loading={generating === 'reimb'} variant="reimb" monthPicker={{ value: reimbursableMonth, onChange: setReimbursableMonth, options: monthOptions, monthsWithData: monthsWithData('reimbursable') }} previewData={buildReimbursablePreview()} onClick={generateReimbursementRequest} />
            <ReportCard t={t} fmtMoney={fmtMoney} icon="📊" title={t('reports.card_rb_csv_title', { year })} body={t('reports.card_rb_csv_body')} buttonLabel={t('reports.download_csv')} disabled={rbReceipts.length === 0 || !!generating} loading={generating === 'reimb-csv'} previewData={buildReceiptPreview(rbReceipts)} onClick={downloadReimbursableCsv} />
          </div>

          <SectionHeader icon="🏠" title={t('reports.section_pers_title')} sub={t('reports.section_pers_sub')} accent={TAG_ACCENTS.personal.hex} marginTop={40} />
          <div style={styles.reportGrid}>
            <ReportCard t={t} fmtMoney={fmtMoney} icon="📄" title={t('reports.card_pers_full_title', { year })} body={t('reports.card_pers_full_body')} buttonLabel={t('reports.download_pdf')} disabled={persReceipts.length === 0 || !!generating} loading={generating === 'pers-full'} variant="pers" previewData={buildReceiptPreview(persReceipts)} onClick={generatePersonalFullYear} />
            <ReportCard t={t} fmtMoney={fmtMoney} icon="📅" title={t('reports.card_pers_monthly_title')} body={t('reports.card_pers_monthly_body')} buttonLabel={t('reports.download_pdf')} disabled={persReceipts.length === 0 || !!generating} loading={generating === 'pers-month'} variant="pers" monthPicker={{ value: personalMonth, onChange: setPersonalMonth, options: monthOptions, monthsWithData: monthsWithData('personal') }} previewData={buildPersonalMonthlyPreview()} onClick={generatePersonalMonthly} />
            <ReportCard t={t} fmtMoney={fmtMoney} icon="📊" title={t('reports.card_pers_csv_title', { year })} body={t('reports.card_pers_csv_body')} buttonLabel={t('reports.download_csv')} disabled={persReceipts.length === 0 || !!generating} loading={generating === 'pers-csv'} variant="pers" previewData={buildReceiptPreview(persReceipts)} onClick={downloadPersonalCsv} />
          </div>

          <div style={styles.disclaimer}>
            <strong>{t('reports.disclaimer_label')}:</strong> {t('reports.disclaimer_body')}
          </div>
        </>
      )}
    </div>
  )
}

function PreviewStat({ label, sub, value, valueColor }) {
  return (
    <div style={styles.previewStat}>
      <div style={styles.previewStatLabel}>{label}</div>
      <div style={{ ...styles.previewStatValue, color: valueColor || plum }}>{value}</div>
      <div style={styles.previewStatSub}>{sub}</div>
    </div>
  )
}

function SectionHeader({ icon, title, sub, accent, marginTop = 0 }) {
  return (
    <div style={{ ...styles.sectionHeader, marginTop: marginTop ? `${marginTop}px` : '0', borderLeftColor: accent }}>
      <span style={styles.sectionEmoji}>{icon}</span>
      <div>
        <div style={styles.sectionTitle}>{title}</div>
        <div style={styles.sectionSub}>{sub}</div>
      </div>
    </div>
  )
}

function ReportCard({ t, fmtMoney, icon, title, body, buttonLabel, disabled, loading, onClick, monthPicker, variant = 'default', previewData }) {
  const [showPreview, setShowPreview] = useState(false)
  const lang = i18n.language || 'en'
  const locale = lang.startsWith('es') ? 'es-US' : 'en-US'
  const monthName = (m) => new Date(2000, m - 1, 1).toLocaleDateString(locale, { month: 'long' })
  const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString(locale, { month: 'short', day: 'numeric' }) : ''
  const btnStyle =
    variant === 'primary' ? styles.btnPrimary :
    variant === 'cc' ? styles.btnCC :
    variant === 'reimb' ? styles.btnReimb :
    variant === 'pers' ? styles.btnPers :
    styles.btnSecondary

  return (
    <div style={styles.reportCard}>
      <div style={styles.reportIcon}>{icon}</div>
      <div style={styles.reportTitle}>{title}</div>
      <div style={styles.reportSub}>{body}</div>
      {monthPicker && (
        <div>
          <select
            style={styles.monthSelect}
            value={monthPicker.value}
            onChange={(e) => monthPicker.onChange(parseInt(e.target.value))}
            disabled={disabled}
          >
            {monthPicker.options.map(m => (
              <option key={m} value={m}>
                {monthName(m)}{monthPicker.monthsWithData?.has(m) ? '' : ` ${t('reports.no_data_suffix')}`}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={styles.cardButtonRow}>
        <button
          style={{ ...btnStyle, opacity: disabled ? 0.4 : 1, cursor: disabled ? 'not-allowed' : 'pointer', flex: 1 }}
          onClick={onClick}
          disabled={disabled}
          title={disabled && !loading ? t('reports.no_data_to_export') : ''}
        >
          {loading ? t('reports.generating') : buttonLabel}
        </button>
        {previewData && (
          <button
            type="button"
            style={{ ...styles.previewBtn, background: showPreview ? '#F3F4F6' : '#fff' }}
            onClick={() => setShowPreview(v => !v)}
            title={showPreview ? t('reports.hide_preview') : t('reports.preview')}
            aria-label={showPreview ? t('reports.hide_preview') : t('reports.preview')}
          >
            👁
          </button>
        )}
      </div>

      {showPreview && previewData && (
        <div style={styles.previewPanel}>
          <div style={styles.previewHead}>
            <span style={styles.previewTotal}>{fmtMoney(previewData.total)}</span>
            <span style={styles.previewCount}>
              {t('reports.preview_count', { count: previewData.count })}
            </span>
          </div>
          {previewData.items && previewData.items.length > 0 ? (
            <div style={styles.previewList}>
              {previewData.items.slice(0, 8).map((it, i) => (
                <div key={i} style={styles.previewItem}>
                  <span style={styles.previewItemDate}>{fmtDate(it.date)}</span>
                  <span style={styles.previewItemLabel}>{it.label}</span>
                  <span style={styles.previewItemAmt}>{fmtMoney(it.amount)}</span>
                </div>
              ))}
              {previewData.items.length > 8 && (
                <div style={styles.previewMore}>
                  {t('reports.preview_more', { count: previewData.items.length - 8 })}
                </div>
              )}
            </div>
          ) : (
            <div style={styles.previewEmpty}>{t('reports.preview_empty')}</div>
          )}
        </div>
      )}
    </div>
  )
}

const styles = {
  page: { padding: '32px 40px 80px', maxWidth: '1200px', margin: '0 auto', fontFamily: "'Plus Jakarta Sans', sans-serif", color: plum },
  header: { marginBottom: '28px' },
  title: { fontFamily: "'Fraunces', serif", fontSize: '36px', fontWeight: 900, letterSpacing: '-1px', color: plum, margin: 0, lineHeight: 1.1 },
  subtitle: { fontSize: '15px', color: '#6B7280', marginTop: '6px', fontWeight: 500, maxWidth: '720px', lineHeight: 1.5 },
  yearRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px' },
  yearLabel: { fontSize: '12px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' },
  yearSelect: { padding: '8px 14px', borderRadius: '8px', border: '1.5px solid #E5E7EB', fontSize: '14px', fontWeight: 700, color: plum, background: '#fff', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" },
  previewCard: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '16px', padding: '20px 24px', marginBottom: '32px' },
  previewRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' },
  previewStat: { display: 'flex', flexDirection: 'column', gap: '4px' },
  previewStatLabel: { fontSize: '11px', color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' },
  previewStatValue: { fontFamily: "'Fraunces', serif", fontSize: '24px', fontWeight: 800, lineHeight: 1 },
  previewStatSub: { fontSize: '12px', color: '#9CA3AF', fontWeight: 500, marginTop: '2px' },
  loading: { padding: '60px 20px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px', paddingTop: '4px', paddingLeft: '10px', borderLeft: '4px solid', borderRadius: '2px' },
  sectionEmoji: { fontSize: '26px' },
  sectionTitle: { fontFamily: "'Fraunces', serif", fontSize: '20px', fontWeight: 800, color: plum, letterSpacing: '-0.3px' },
  sectionSub: { fontSize: '13px', color: '#6B7280', fontWeight: 500 },
  reportGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px', marginBottom: '16px' },
  reportCard: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '16px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px' },
  reportIcon: { fontSize: '30px', lineHeight: 1 },
  reportTitle: { fontFamily: "'Fraunces', serif", fontSize: '16px', fontWeight: 800, color: plum, letterSpacing: '-0.3px', lineHeight: 1.3 },
  reportSub: { fontSize: '13px', color: '#6B7280', fontWeight: 500, lineHeight: 1.5, flex: 1 },
  monthSelect: { width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #E5E7EB', fontSize: '13px', fontWeight: 600, color: plum, background: '#fff', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" },
  cardButtonRow: { display: 'flex', gap: '8px', alignItems: 'center' },
  previewBtn: { width: '42px', height: '42px', flexShrink: 0, background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: '100px', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, transition: 'background 0.15s' },
  previewPanel: { marginTop: '12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '12px' },
  previewHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #E5E7EB' },
  previewTotal: { fontFamily: "'Fraunces', serif", fontSize: '18px', fontWeight: 800, color: plum, letterSpacing: '-0.3px' },
  previewCount: { fontSize: '11px', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
  previewList: { display: 'flex', flexDirection: 'column', gap: '6px' },
  previewItem: { display: 'grid', gridTemplateColumns: '54px 1fr auto', gap: '8px', fontSize: '12px', alignItems: 'center' },
  previewItemDate: { color: '#9CA3AF', fontWeight: 500 },
  previewItemLabel: { color: plum, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  previewItemAmt: { color: plum, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  previewMore: { fontSize: '11px', color: '#9CA3AF', fontStyle: 'italic', paddingTop: '6px', textAlign: 'center' },
  previewEmpty: { fontSize: '12px', color: '#9CA3AF', fontStyle: 'italic', textAlign: 'center', padding: '12px' },
  btnPrimary: { padding: '11px', background: teal, color: '#042f2e', border: 'none', borderRadius: '100px', fontSize: '13px', fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", boxShadow: `0 4px 14px ${teal}40` },
  btnSecondary: { padding: '11px', background: '#fff', color: plum, border: '1.5px solid #E5E7EB', borderRadius: '100px', fontSize: '13px', fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" },
  btnCC: { padding: '11px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: '100px', fontSize: '13px', fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", boxShadow: '0 4px 14px rgba(37, 99, 235, 0.3)' },
  btnReimb: { padding: '11px', background: '#F59E0B', color: '#422006', border: 'none', borderRadius: '100px', fontSize: '13px', fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", boxShadow: '0 4px 14px rgba(245, 158, 11, 0.3)' },
  btnPers: { padding: '11px', background: '#A855F7', color: '#fff', border: 'none', borderRadius: '100px', fontSize: '13px', fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", boxShadow: '0 4px 14px rgba(168, 85, 247, 0.3)' },
  disclaimer: { fontSize: '12px', color: '#9CA3AF', textAlign: 'center', padding: '16px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '12px', lineHeight: 1.6, marginTop: '24px' },
}
